// Tiny Web Audio synth primitives. Pure functions over an AudioContext —
// build a graph, schedule, and return its end time so the caller can chain
// or release. No global state; the AudioContext is passed in by the
// AudioManager that holds the live one for the page.
//
// Why synth and not loaded files? Zero asset footprint, deterministic,
// tunable from code. When real SFX assets land they replace the
// preset functions in sfx.ts one-for-one (the call sites don't change).

export type Wave = 'sine' | 'square' | 'sawtooth' | 'triangle';

export interface BeepParams {
  /** Hz. */
  freq: number;
  /** Seconds. */
  duration: number;
  /** Attack in seconds. */
  attack?: number;
  /** Release in seconds. */
  release?: number;
  /** Peak gain 0..1. */
  gain?: number;
  /** Oscillator shape. */
  wave?: Wave;
}

/** Single oscillator beep with linear attack + exponential release. */
export function beep(
  ctx: AudioContext,
  out: AudioNode,
  p: BeepParams,
  startAt: number = ctx.currentTime,
): number {
  const attack = p.attack ?? 0.005;
  const release = p.release ?? 0.08;
  const gain = p.gain ?? 0.3;
  const wave = p.wave ?? 'sine';

  const osc = ctx.createOscillator();
  osc.type = wave;
  osc.frequency.setValueAtTime(p.freq, startAt);

  const g = ctx.createGain();
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + attack);
  g.gain.linearRampToValueAtTime(gain, startAt + attack + p.duration);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + attack + p.duration + release);

  osc.connect(g).connect(out);
  osc.start(startAt);
  osc.stop(startAt + attack + p.duration + release + 0.02);
  return startAt + attack + p.duration + release;
}

export interface SweepParams {
  fromFreq: number;
  toFreq: number;
  duration: number;
  gain?: number;
  wave?: Wave;
}

/** Frequency sweep from `fromFreq` to `toFreq` over `duration`. */
export function sweep(
  ctx: AudioContext,
  out: AudioNode,
  p: SweepParams,
  startAt: number = ctx.currentTime,
): number {
  const gain = p.gain ?? 0.25;
  const osc = ctx.createOscillator();
  osc.type = p.wave ?? 'sine';
  osc.frequency.setValueAtTime(p.fromFreq, startAt);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(20, p.toFreq),
    startAt + p.duration,
  );

  const g = ctx.createGain();
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + p.duration);

  osc.connect(g).connect(out);
  osc.start(startAt);
  osc.stop(startAt + p.duration + 0.02);
  return startAt + p.duration;
}

export interface NoiseBurstParams {
  duration: number;
  /** Lowpass cutoff Hz; default unfiltered. */
  lowpass?: number;
  /** Highpass cutoff Hz; default unfiltered. */
  highpass?: number;
  gain?: number;
}

/** White noise burst with optional band filtering. Good for impacts. */
export function noiseBurst(
  ctx: AudioContext,
  out: AudioNode,
  p: NoiseBurstParams,
  startAt: number = ctx.currentTime,
): number {
  const sampleRate = ctx.sampleRate;
  const samples = Math.max(1, Math.floor(sampleRate * p.duration));
  const buf = ctx.createBuffer(1, samples, sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;

  let node: AudioNode = src;
  if (p.lowpass !== undefined) {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = p.lowpass;
    node.connect(lp);
    node = lp;
  }
  if (p.highpass !== undefined) {
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = p.highpass;
    node.connect(hp);
    node = hp;
  }

  const g = ctx.createGain();
  const gain = p.gain ?? 0.3;
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + p.duration);

  node.connect(g).connect(out);
  src.start(startAt);
  src.stop(startAt + p.duration + 0.02);
  return startAt + p.duration;
}

export interface ChordParams {
  /** Hz array of simultaneously played frequencies. */
  freqs: readonly number[];
  duration: number;
  gain?: number;
  wave?: Wave;
}

/** Stacked beeps at multiple frequencies. */
export function chord(
  ctx: AudioContext,
  out: AudioNode,
  p: ChordParams,
  startAt: number = ctx.currentTime,
): number {
  const per = (p.gain ?? 0.25) / Math.max(1, p.freqs.length);
  let end = startAt;
  for (const f of p.freqs) {
    end = Math.max(
      end,
      beep(ctx, out, { freq: f, duration: p.duration, gain: per, wave: p.wave }, startAt),
    );
  }
  return end;
}
